/**
 * Tela /import-mappings (TH-53217 / M2.E7.S3.1).
 *
 * Lista os mapeamentos de planilha do tenant. Cada mapping é um nome
 * + um `config` JSON descrevendo qual coluna da planilha vira qual campo
 * do produto (sku, name, price, promo_price...). O backend já existe
 * (TH-53208) com endpoints CRUD em /api/import-mappings.
 *
 * O schema do `config` exige no mínimo sku, name e price com required=true
 * (validado server-side em ImportMappingBase). A coluna "Campos" mostra
 * a contagem total para o operador identificar mapping rico vs minimal.
 *
 * Acesso: super_admin, tenant_admin, store_manager, operator (mesmo do
 * router backend `_check_can_manage`). Roles abaixo veem 403 inline.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Pencil, Trash2 } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import { useAuth } from "@/hooks/useAuth";

interface ImportMappingRead {
  id: string;
  name: string;
  config: Record<string, { column: string; required?: boolean; format?: string }>;
  created_at: string;
}

const ROLES_AUTHORIZED = new Set([
  "super_admin",
  "tenant_admin",
  "store_manager",
  "operator",
]);

async function fetchMappings(): Promise<ImportMappingRead[]> {
  const res = await fetch("/api/import-mappings", { credentials: "include" });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as ImportMappingRead[];
}

async function deleteMapping(id: string): Promise<void> {
  const res = await fetch(`/api/import-mappings/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function ImportMappingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = user ? ROLES_AUTHORIZED.has(user.role) : false;

  const [confirmDelete, setConfirmDelete] = useState<ImportMappingRead | null>(
    null,
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["import-mappings"],
    queryFn: fetchMappings,
    staleTime: 30_000,
    enabled: canManage,
  });

  const remove = useMutation({
    mutationFn: (m: ImportMappingRead) => deleteMapping(m.id),
    onSuccess: () => {
      setConfirmDelete(null);
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ["import-mappings"] });
    },
    onError: (err: Error) => setDeleteError(err.message),
  });

  const rows = useMemo(() => query.data ?? [], [query.data]);

  if (user && !canManage) {
    return (
      <RequireAuth>
        <AppLayout
          breadcrumb="OPERAÇÃO / IMPORTAÇÕES / MAPEAMENTOS"
          title="Acesso negado"
        >
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Esta tela está disponível para super_admin, tenant_admin,
              store_manager ou operator.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  const columns: DataTableColumn<ImportMappingRead>[] = [
    {
      key: "name",
      header: "Nome",
      cell: (m) => (
        <Link
          to="/import-mappings/$mappingId"
          params={{ mappingId: m.id }}
          className="font-medium text-[var(--color-foreground)] hover:text-[var(--color-primary)] hover:underline"
        >
          {m.name}
        </Link>
      ),
    },
    {
      key: "fields_count",
      header: "Campos",
      align: "right",
      mono: true,
      cell: (m) => Object.keys(m.config).length,
    },
    {
      key: "created_at",
      header: "Criado em",
      mono: true,
      cell: (m) => (
        <span className="text-[var(--color-muted-foreground)]">
          {formatDate(m.created_at)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (m) => (
        <div className="flex justify-end gap-1">
          <Button
            size="icon"
            variant="ghost"
            aria-label="Editar"
            onClick={() =>
              navigate({
                to: "/import-mappings/$mappingId",
                params: { mappingId: m.id },
              })
            }
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Remover"
            className="text-[var(--color-destructive)]"
            onClick={() => {
              setConfirmDelete(m);
              setDeleteError(null);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const subtitle = query.data
    ? `${query.data.length} ${query.data.length === 1 ? "mapeamento" : "mapeamentos"}`
    : undefined;

  const headerActions = (
    <Link to="/import-mappings/new">
      <Button>+ Novo mapeamento</Button>
    </Link>
  );

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / IMPORTAÇÕES / MAPEAMENTOS"
        title="Mapeamentos"
        actions={headerActions}
      >
        <div className="mx-auto max-w-5xl">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Layouts de planilha do ERP que descrevem como cada coluna vira
            campo de produto no Priceshow. São reutilizados a cada importação.
          </p>

          {query.error ? (
            <div className="mt-4 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]">
              erro ao carregar mapeamentos
            </div>
          ) : null}

          <div className="mt-6">
            <DataTable<ImportMappingRead>
              columns={columns}
              rows={rows}
              title="Mapeamentos"
              subtitle={subtitle}
              rowKey={(m) => m.id}
              emptyMessage={
                query.isLoading
                  ? "carregando…"
                  : "nenhum mapeamento cadastrado — comece com + Novo mapeamento"
              }
            />
          </div>
        </div>

        <Dialog
          open={!!confirmDelete}
          onOpenChange={(o) => {
            if (!o) {
              setConfirmDelete(null);
              setDeleteError(null);
            }
          }}
        >
          <DialogContent>
            <DialogTitle>Remover mapeamento</DialogTitle>
            <DialogDescription>
              Confirma a exclusão de{" "}
              <strong className="text-[var(--color-foreground)]">
                {confirmDelete?.name}
              </strong>
              ? O backend bloqueia a operação caso o mapping já tenha sido usado
              em algum batch de importação.
            </DialogDescription>
            {deleteError ? (
              <p
                role="alert"
                className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]"
              >
                {deleteError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setConfirmDelete(null);
                  setDeleteError(null);
                }}
                disabled={remove.isPending}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={() => confirmDelete && remove.mutate(confirmDelete)}
                disabled={remove.isPending}
              >
                {remove.isPending ? "Removendo…" : "Remover"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </AppLayout>
    </RequireAuth>
  );
}

